import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { getPriceCut } from 'components/marketplace/helpers/certaintyPanel';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { AVAILABLE_SOON_DISCLOSURE } from 'components/marketplace/shared/availableSoon';
import { isPropertyAvailableForPurchase } from 'components/properties/helpers/property';
import { useTouring } from 'components/self-tour/hooks/useTouring';

import { BuyDirectContextModal } from '../PropertySection/BuyDirectContextModal';
import { SelfTourDialog } from '../PropertySection/SelfTourDialog';
import { TourWithAgentModal } from '../PropertySection/TourWithAgentModal';
import type { MarketplaceProperty } from '../types';

type TourAction = NonNullable<ReturnType<typeof useTouring>>['primaryAction'];

const TourActionButton = ({
  action,
  label = action.text,
  variant = action.variant,
}: {
  action: TourAction;
  label?: string;
  variant?: TourAction['variant'];
}) => (
  <Button
    analyticsName={action.analyticsName}
    data-analytics-name={action.analyticsName}
    aria-label={label}
    variant={variant}
    size="md"
    onClick={action.onClick}
    disabled={action.disabled}
    href={action.disabled ? undefined : action.href}
    target={action.target}
    rel="noopener"
    className={cn(
      // 16px labels wrap inside a half-width phone button ("Tour with" / "agent").
      'w-full min-w-0 whitespace-nowrap rounded-xl px-2 text-label-sm transition-none not-disabled:active:scale-100',
      // Figma spec: blue fill with a soft drop shadow and a lighter inner highlight.
      variant === 'primary' &&
        'bg-[#0040E6] shadow-[0_2px_3px_0_rgba(0,64,230,0.15),inset_0_2px_2px_0_#0266F2]',
      // Figma spec: warm gray stroke with a soft drop shadow.
      variant === 'secondary' && 'border-[#D7D4CD] shadow-[0_2px_3px_0_rgba(137,134,127,0.15)]',
    )}
  >
    {label}
  </Button>
);

export const PdpPriceCard = ({ property }: { property: MarketplaceProperty }) => {
  const touring = useTouring(property, { placement: 'main' });
  const quotesBuyDirect = property.listingState === 'ON_THE_MARKET' && touring?.showBuyDirect;
  const price = quotesBuyDirect
    ? (property.buyDirectPrice ?? property.listPrice)
    : getDisplayPrice(property, 'listed');
  const monthlyPayment = quotesBuyDirect
    ? (property.buyDirectEstimatedMonthlyPayment ?? property.estimatedMonthlyPayment)
    : property.estimatedMonthlyPayment;
  let priceLabel = 'Price not available';
  if (price != null) {
    priceLabel = formatCurrency(price);
  } else if (property.listingState === 'AVAILABLE_SOON') {
    priceLabel = 'Price pending';
  }
  const availableForPurchase = isPropertyAvailableForPurchase(property);
  const priceCut = availableForPurchase ? getPriceCut(property.listingHistory) : null;
  const savings = availableForPurchase && touring?.showBuyDirect ? property.buyDirectSavings : null;
  const showSavings = savings != null && savings > 0;

  return (
    <>
      <Col
        as="section"
        aria-label="Home price"
        className="relative w-full overflow-hidden rounded-xl"
      >
        {quotesBuyDirect && (
          <>
            <Text className="relative z-10 flex h-9 items-center px-4 text-label-sm text-[#020E60]">
              Buy with Opendoor
            </Text>
          </>
        )}
        {/* Figma: backing rectangle, #231E1B at 3% with a 1px inner stroke (#EAE8E0) and a deep inner shadow. */}
        <Col className="relative w-full rounded-xl bg-[rgba(35,30,27,0.03)] shadow-[inset_0_0_0_1px_#EAE8E0,inset_0_200px_24px_0_rgba(35,30,27,0.06)]">
          {quotesBuyDirect && (
            // Figma: blue rectangle (#CDE4FF) behind the pricing card with an inner shadow for depth.
            <Box
              aria-hidden
              className="absolute inset-x-0 -top-9 bottom-[52px] rounded-xl bg-[#CDE4FF] shadow-[inset_0_-192px_12px_0_rgba(147,192,245,0.6)]"
            />
          )}
          <Col className="@container relative w-full gap-7 rounded-xl border border-[#EAE8E0] bg-od-bg-primary p-5 lg:p-7">
            <Box className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
              <Col className="min-w-0 gap-3">
                {showSavings && (
                  <Text className="text-label-sm leading-[1.25] text-od-content-accent-brand">
                    Save {formatCurrency(savings)}
                  </Text>
                )}
                {!showSavings && priceCut && (
                  <Text className="text-label-sm text-od-content-accent-brand">
                    {formatCurrency(priceCut.cutCents / 100)} price drop
                  </Text>
                )}
                <Text
                  as="h2"
                  className="text-[36px] font-medium leading-none tracking-[-0.03em] text-blue-950"
                >
                  {priceLabel}
                </Text>
                {showSavings && priceCut && (
                  <Text className="text-body-sm text-od-content-secondary">
                    {formatCurrency(priceCut.cutCents / 100)} price drop
                  </Text>
                )}
              </Col>
              {availableForPurchase && monthlyPayment != null && (
                <Col className="max-w-32 items-end gap-3 text-right @[24rem]:max-w-none">
                  <Text className="text-label-sm leading-[1.25] text-od-content-secondary">
                    Est. Monthly Payment
                  </Text>
                  <Text className="text-label-xl leading-none text-od-content-primary">
                    {formatCurrency(monthlyPayment)}
                  </Text>
                </Col>
              )}
            </Box>
            {property.listingState === 'AVAILABLE_SOON' && (
              <Text as="p" className="text-body-sm text-od-content-tertiary">
                {AVAILABLE_SOON_DISCLOSURE}
              </Text>
            )}
            {touring && (
              <Box className={touring.secondaryAction ? 'grid grid-cols-2 gap-3' : 'w-full'}>
                {touring.secondaryAction && (
                  <TourActionButton
                    action={touring.secondaryAction}
                    variant={
                      touring.tourKind === 'selfTour' ? 'primary' : touring.secondaryAction.variant
                    }
                    label={touring.tourKind === 'selfTour' ? 'Take a self tour' : undefined}
                  />
                )}
                <TourActionButton
                  action={touring.primaryAction}
                  variant={touring.showBuyDirect ? 'secondary' : touring.primaryAction.variant}
                  label={
                    !touring.showBuyDirect && touring.tourKind === 'selfTour'
                      ? 'Take a self tour'
                      : undefined
                  }
                />
              </Box>
            )}
          </Col>
          {availableForPurchase && quotesBuyDirect && property.listPrice != null && (
            <Row className="h-[52px] items-center justify-between gap-4 px-4">
              <Text className="text-body-sm font-normal text-[#3C3733]">List price:</Text>
              <Text className="text-label-sm font-medium text-[#3C3733]">
                {formatCurrency(property.listPrice)}
              </Text>
            </Row>
          )}
        </Col>
      </Col>
      {touring && (
        <>
          {touring.tourKind === 'tourWithAgent' && (
            <TourWithAgentModal
              property={property}
              isOpen={touring.isDialogOpen}
              onClose={touring.closeDialog}
            />
          )}
          <SelfTourDialog
            isOpen={touring.isSelfTourDialogOpen}
            onClose={touring.closeSelfTourDialog}
            onOpenRequest={touring.openSelfTourDialog}
            placement="main"
            addressToken={property.addressToken}
            slug={property.slug}
            tourBookingListingId={property.tourBookingListingId}
            addressLine={[property.street, property.city].filter(Boolean).join(', ')}
          />
          <BuyDirectContextModal
            isOpen={touring.isBuyDirectContextOpen}
            onClose={touring.closeBuyDirectContext}
            continueHref={touring.buyDirectContextHref}
          />
        </>
      )}
    </>
  );
};
